import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { endpoints } from '@/shared/api/endpoints'
import { onSessionEvent, session } from '@/shared/api/client'
import { useMe, useModules } from '@/shared/api/queries'
import type { ModuleKey, User } from '@/shared/api/types'
import { abilityCan, type Capability } from './capabilities'

interface AuthValue {
  user: User | null
  /** Session en cours de vérification au démarrage. */
  booting: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  can: (c: Capability) => boolean
  /** Un module coupé pendant la session est retiré dès le premier 403 reçu. */
  hasModule: (m: ModuleKey) => boolean
  stationIds: number[]
  /** Compte rattaché à des stations : il ne voit pas tout le réseau. */
  isScoped: boolean
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [hasToken, setHasToken] = useState(() => session.token !== null)
  const [disabledModules, setDisabledModules] = useState<Set<string>>(() => new Set())

  // Sans jeton, pas d'appel : un 401 ici viderait le cache et relancerait la requête en boucle.
  const me = useMe(hasToken)
  const user = hasToken ? (me.data?.data ?? null) : null
  const modules = useModules(!!user)

  // Un 401 n'importe où ferme la session ; un 403-module masque l'écran.
  useEffect(
    () =>
      onSessionEvent((event) => {
        if (event.type === 'unauthenticated') {
          setHasToken(false)
          qc.clear()
        } else {
          setDisabledModules((prev) => new Set(prev).add(event.module))
        }
      }),
    [qc],
  )

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await endpoints.auth.login(email, password)
      session.open(res.token)
      setDisabledModules(new Set())
      qc.setQueryData(['me'], { data: res.user })
      setHasToken(true)
    },
    [qc],
  )

  const logout = useCallback(async () => {
    try {
      await endpoints.auth.logout()
    } catch {
      /* jeton déjà révoqué : on ferme quand même localement */
    }
    session.close()
    setHasToken(false)
    qc.clear()
  }, [qc])

  const value = useMemo<AuthValue>(() => {
    const enabled = new Set(modules.data?.data.filter((m) => m.is_enabled).map((m) => m.key) ?? [])
    return {
      user,
      booting: hasToken && me.isPending,
      login,
      logout,
      can: (c) => abilityCan(user?.abilities ?? [], c),
      hasModule: (m) => {
        if (disabledModules.has(m)) return false
        // Tout compte lit la liste des modules ; un module coupé pendant la
        // session est retiré dès le premier 403 (docs/API.md §4).
        if (!modules.data) return true
        return enabled.has(m)
      },
      stationIds: user?.stations?.map((s) => s.id) ?? [],
      // Le périmètre est une permission, pas un rôle : sans `network.view-all`,
      // le compte ne voit que ses stations de rattachement.
      isScoped: !(user?.abilities ?? []).includes('network.view-all'),
    }
  }, [user, hasToken, me.isPending, login, logout, disabledModules, modules.data])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// oxlint-disable-next-line react/only-export-components -- hooks co-localisés avec leur Provider par convention
export function useAuth(): AuthValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth doit être utilisé dans <AuthProvider>')
  return v
}

/** Session garantie non nulle — dans les écrans protégés uniquement. */
// oxlint-disable-next-line react/only-export-components -- hooks co-localisés avec leur Provider par convention
export function useUser(): User {
  const { user } = useAuth()
  if (!user) throw new Error('Aucune session active')
  return user
}
